import type { Ref, WatchCallback } from 'vue';
import { onUnmounted, ref, watch } from 'vue';

import { useCurrentUserStore } from '@/stores/CurrentUser';

const unwatchCallbacks: Ref<{ [uid: string]: () => void | undefined }> = ref(
  {},
);

/**
 * Sets up a watcher on the selected domain id.
 *
 * If `watcher` is provider, the watcher will be set up immediately. Otherwise,
 * the caller is expected to call the returned callback to start the watcher.
 *
 * @param watcher A vuex watcher that will run when the selected domain id changes
 * @returns a callback to start the watcher
 */
export const useSelectedDomain = (
  watcher?: WatchCallback,
): ((watcher: WatchCallback) => void) => {
  const { selectedDomainId } = useCurrentUserStore();

  let uid: string;
  onUnmounted(() => uid && unwatchCallbacks.value[uid]?.());

  const watchSelectedDomain = (watcher: WatchCallback) => {
    uid = watcher.toString();
    unwatchCallbacks.value[uid]?.();
    unwatchCallbacks.value[uid] = watch(selectedDomainId, watcher);
  };

  if (watcher) {
    watchSelectedDomain(watcher);
  }

  return watchSelectedDomain;
};
